import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/terms.scss";

function Terms() {
  const lastUpdated = siteConfig.policies?.lastUpdated || "August 14, 2026";

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="terms policy-page">
      <header className="policy-page__hero">
        <p className="policy-page__kicker">Legal</p>
        <h1>Terms &amp; conditions</h1>
        <p>
          The terms that apply when you visit, purchase from or otherwise use
          the {siteConfig.brandName} online store.
        </p>
        <span>Last updated {lastUpdated}</span>
      </header>

      <div className="policy-page__layout">
        <nav className="policy-page__nav" aria-label="Terms sections">
          <span>On this page</span>
          <a href="#terms-use">Using our store</a>
          <a href="#terms-orders">Orders &amp; payment</a>
          <a href="#terms-products">Products</a>
          <a href="#terms-liability">Liability</a>
          <a href="#terms-contact">Contact</a>
        </nav>

        <article className="policy-page__content">
          <section className="policy-page__lead" aria-label="Terms overview">
            <p>
              These terms form an agreement between you and {siteConfig.brandName}.
              By accessing our store or placing an order, you agree to them and
              to the policies linked here. If you do not agree, please do not use
              the store.
            </p>
          </section>

          <section id="terms-use">
            <p className="policy-page__number">01</p>
            <h2>Eligibility and responsible use</h2>
            <p>
              You must be able to enter a binding contract where you live, or
              use the store with the involvement of a parent or guardian. You
              agree to provide accurate information and not misuse the store,
              interfere with its operation, attempt unauthorized access, upload
              harmful code or use our services for an unlawful purpose.
            </p>
            <p>
              We may limit, suspend or refuse access when reasonably necessary
              to protect customers, our business or the integrity of the store.
            </p>
          </section>

          <section id="terms-orders">
            <p className="policy-page__number">02</p>
            <h2>Orders, prices and payment</h2>
            <p>
              Your checkout submission is an offer to purchase. An order is
              accepted when we send a dispatch confirmation; the automated
              acknowledgement only confirms receipt. We may decline or cancel an
              order for stock, payment, pricing, shipping-restriction or suspected
              fraud reasons. If payment was captured, the affected amount will be
              returned to the original method.
            </p>
            <p>
              Prices are shown in {siteConfig.currency} and may change without
              notice. Taxes and delivery charges are displayed at checkout where
              applicable. You confirm that you are authorized to use the payment
              method supplied and that billing details are complete and current.
            </p>
          </section>

          <section id="terms-products">
            <p className="policy-page__number">03</p>
            <h2>Products and availability</h2>
            <p>
              We aim to describe products, sizes, ingredients and colors
              accurately, but screens and natural materials can create minor
              variation. Packaging or ingredients may be updated; always review
              the label received before use. Availability is not guaranteed and
              we may limit quantities. Product information is educational and is
              not medical advice. Discontinue use if irritation occurs and seek
              professional advice for a health concern.
            </p>
          </section>

          <section>
            <p className="policy-page__number">04</p>
            <h2>Shipping, title and returns</h2>
            <p>
              Delivery estimates are not guarantees. Risk of loss passes as
              permitted by applicable law after delivery to the address you
              supplied. Review our <Link to="/shipping">shipping policy</Link>{" "}
              for timing and charges. Eligible purchases may be returned under
              our <Link to="/returns">returns policy</Link>, which forms part of
              these terms.
            </p>
          </section>

          <section>
            <p className="policy-page__number">05</p>
            <h2>Content and intellectual property</h2>
            <p>
              The store and its text, photography, graphics, product names,
              design and software are owned by or licensed to us and protected
              by intellectual-property law. We grant you a limited, revocable,
              non-transferable right to use the store for personal shopping. You
              may not reproduce, sell, scrape, modify or commercially exploit its
              content without written permission.
            </p>
            <p>
              If you submit a review or other content, you confirm that it is
              yours, lawful and accurate, and grant us a worldwide, royalty-free
              license to use it in connection with our business. We may remove
              content that violates these terms.
            </p>
          </section>

          <section>
            <p className="policy-page__number">06</p>
            <h2>Third-party services</h2>
            <p>
              Our store may use or link to services operated by others. Their
              own terms and privacy practices apply, and we are not responsible
              for third-party content, availability or conduct. A link does not
              imply endorsement.
            </p>
          </section>

          <section id="terms-liability">
            <p className="policy-page__number">07</p>
            <h2>Disclaimers and limitation of liability</h2>
            <p>
              To the fullest extent allowed by law, the store is provided “as
              available” without implied warranties beyond those that cannot be
              excluded. We are not liable for indirect, incidental, special or
              consequential loss arising from store use. Our aggregate liability
              for a claim relating to a purchase will not exceed the amount paid
              for the affected product. Nothing here excludes liability that
              cannot legally be limited or your mandatory consumer rights.
            </p>
          </section>

          <section id="terms-contact">
            <p className="policy-page__number">08</p>
            <h2>Changes, law and contact</h2>
            <p>
              We may revise these terms by posting an updated version and date.
              The law and courts that apply will be determined by our place of
              establishment, subject to any mandatory protections in your home
              jurisdiction. If one provision is unenforceable, the rest remain
              effective. Questions may be sent to{" "}
              <a href={`mailto:${siteConfig.contact.email}`}>
                {siteConfig.contact.email}
              </a>.
            </p>
          </section>

          <div className="policy-page__related">
            <span>Related policies</span>
            <Link to="/policies" onClick={toTop}>All policies</Link>
            <Link to="/privacy" onClick={toTop}>Privacy</Link>
            <Link to="/orders" onClick={toTop}>Orders</Link>
          </div>
        </article>
      </div>
    </main>
  );
}

export default Terms;
